import { useState } from "react";
import { Phone, MessageCircle, Mail, MapPin, Clock, Send } from "lucide-react";
import { COMPANY, telLink, waLink } from "../lib/config";
import { submitContact } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

export default function Contact() {
  const [f, setF] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!f.name.trim()) e.name = "Ad soyad gerekli";
    if (!f.phone.trim()) e.phone = "Telefon gerekli";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Geçerli e-posta girin";
    if (!f.subject.trim()) e.subject = "Konu gerekli";
    if (f.message.trim().length < 10) e.message = "Mesaj en az 10 karakter";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await submitContact(f);
      toast.success("Mesajınız alındı. En kısa sürede dönüş yapacağız.");
      setF({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Mesaj gönderilemedi. Lütfen tekrar deneyin.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="contact-page" className="pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <div className="label-caps mb-3">İletişim</div>
          <h1 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight mb-4">Size Nasıl Yardımcı Olabiliriz?</h1>
          <p className="text-slate-600">Aşağıdaki formu doldurarak ya da doğrudan telefon / WhatsApp üzerinden bize ulaşabilirsiniz. Ekibimiz en kısa sürede sizinle iletişime geçecektir.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-4">
            {[
              { icon: Phone, title: "Telefon", value: COMPANY.phone, href: telLink() },
              { icon: MessageCircle, title: "WhatsApp", value: COMPANY.whatsapp, href: waLink() },
              { icon: Mail, title: "E-Posta", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
              { icon: MapPin, title: "Adres", value: COMPANY.address },
              { icon: Clock, title: "Çalışma Saatleri", value: COMPANY.hours },
            ].map((c, i) => (
              <a key={i} href={c.href || "#"} data-testid={`contact-info-${i}`} className="flex items-start gap-4 p-5 bg-white border border-slate-100 rounded-xl hover:border-[#C0562F]/40 transition-colors">
                <div className="w-11 h-11 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0"><c.icon className="w-4 h-4" /></div>
                <div>
                  <div className="text-[11px] tracking-widest uppercase text-slate-500 font-semibold">{c.title}</div>
                  <div className="text-sm text-slate-800 font-medium mt-0.5">{c.value}</div>
                </div>
              </a>
            ))}
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="label-caps block mb-2">Ad Soyad</label>
                <Input data-testid="contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
                {errors.name && <div className="text-xs text-red-600 mt-1">{errors.name}</div>}
              </div>
              <div>
                <label className="label-caps block mb-2">Telefon</label>
                <Input data-testid="contact-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
                {errors.phone && <div className="text-xs text-red-600 mt-1">{errors.phone}</div>}
              </div>
              <div>
                <label className="label-caps block mb-2">E-posta</label>
                <Input data-testid="contact-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                {errors.email && <div className="text-xs text-red-600 mt-1">{errors.email}</div>}
              </div>
              <div>
                <label className="label-caps block mb-2">Konu</label>
                <Input data-testid="contact-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
                {errors.subject && <div className="text-xs text-red-600 mt-1">{errors.subject}</div>}
              </div>
              <div className="md:col-span-2">
                <label className="label-caps block mb-2">Mesaj</label>
                <Textarea data-testid="contact-message" rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
                {errors.message && <div className="text-xs text-red-600 mt-1">{errors.message}</div>}
              </div>
            </div>
            <button type="submit" disabled={sending} data-testid="contact-submit" className="mt-6 btn-accent px-8 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 disabled:opacity-60">
              <Send className="w-4 h-4" /> {sending ? "Gönderiliyor…" : "Gönder"}
            </button>
          </form>
        </div>

        <div className="mt-14 rounded-2xl overflow-hidden border border-slate-200 aspect-[16/6]">
          <iframe title="Ofis Konumu" src={COMPANY.mapEmbed} className="w-full h-full" loading="lazy" />
        </div>
      </div>
    </div>
  );
}
